'use client';

import { useMemo } from 'react';

import {
	useCareerOfferAnalytics,
	useCareerOfferVisibilityOnce,
} from '@/features/career-offer-analytics';
import {
	buildCareerOfferBannerHref,
	isCareerOfferBannerHref,
} from '@/features/system-alerts/model/advertisement';
import { createCareerOfferBannerTracker } from '@/features/system-alerts/model/advertisement-tracking';
import { Button, XMarkIcon } from '@/shared';
import type { SystemNotificationV1 } from '@/shared/api';

export function AdvertisementBanner({
	notification,
	onDismiss,
}: {
	notification: SystemNotificationV1;
	onDismiss: () => void;
}) {
	const href = buildCareerOfferBannerHref(notification.linkUrl);
	const trackCareerOffer = isCareerOfferBannerHref(href);
	const analytics = useCareerOfferAnalytics();
	const tracker = useMemo(
		() => createCareerOfferBannerTracker(notification.id, analytics, href),
		[analytics, href, notification.id],
	);
	const ref = useCareerOfferVisibilityOnce(() => {
		if (trackCareerOffer) tracker.impression();
	});

	return (
		<div ref={ref} className="relative flex w-full overflow-hidden rounded-lg">
			<div className="absolute inset-0 z-0 rounded-lg bg-white" />
			<div className="relative flex w-full items-center justify-between gap-2 rounded-lg border border-indigo bg-indigo/10 px-4 py-2">
				<div className="mx-auto flex min-w-0 flex-1 flex-col items-center justify-center gap-2 pl-1 pr-2 text-gray-2 md:flex-row">
					<span className="line-clamp-3 md:line-clamp-none md:truncate">
						{notification.widgetTitle || notification.description || ''}
					</span>
					<Button
						href={href}
						variant="outline"
						size="sm"
						onClick={trackCareerOffer ? tracker.click : undefined}
					>
						Подробнее
					</Button>
				</div>
				<button type="button" onClick={onDismiss} aria-label="Закрыть рекламу">
					<XMarkIcon className="size-4 flex-shrink-0" aria-hidden="true" />
				</button>
			</div>
		</div>
	);
}
